import { notifySessionExpired } from '../auth/sessionExpired'

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

const FETCH_TIMEOUT_MS = 30_000

/**
 * Thin fetch wrapper that injects the Bearer JWT token when present
 * and throws on non-2xx responses with the error body as the message.
 * Requests time out after 30 seconds.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = sessionStorage.getItem('jwt')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  // Don't forward a stale token on auth handshake endpoints — the nonce flow is
  // unauthenticated by design, and sending an expired/invalid JWT can cause the
  // security filter to leave the request in a partially-authenticated state.
  const isAuthEndpoint = path.startsWith('/api/auth/')
  if (token && !isAuthEndpoint) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers,
      signal: options.signal ?? controller.signal,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      // A 401 on an already-authenticated request (not the auth handshake
      // endpoints themselves, which legitimately 401 during sign-in/refresh
      // failures) means the backend has rejected this session outright —
      // expired, revoked, or otherwise invalid. Tell AuthContext so it can
      // log out and return to the login screen instead of leaving the app
      // silently stuck making requests that will never succeed.
      if (res.status === 401 && !isAuthEndpoint) {
        notifySessionExpired()
      }
      throw new Error(`${res.status}: ${text}`)
    }
    return res.json() as Promise<T>
  } finally {
    clearTimeout(timeoutId)
  }
}
